// stores/organizationStore.js
import { defineStore } from 'pinia'
import { useUserStore } from './userStore'
import * as organizationApi from '@/api/organizationApi.js'

export const useOrganizationStore = defineStore('organization', {
    state: () => ({
        organizationInfo: null,
        departments: [],
        positions: [],
        statuses: [],
        loading: false,
        error: null,
        _authStore: null,
        _initialized: false
    }),
    getters: {
        isLoading: (state) => state.loading,
        employees: (state) => {
            let list = []
            state.departments.forEach(dep => {
                dep.employee.forEach(em => {
                    list.push(em)
                })
                dep.child.forEach(childDep => {
                    childDep.employee.forEach(childEm => {
                        list.push(childEm)
                    })
                })                
            })
            return list
        },
        department: (state) => {
            return (id) => {
                const foundDep = state.departments.find(dep => dep.id == id)
                if (foundDep) return foundDep
                
                for (const dep of state.departments) {
                    const foundChild = dep.child.find(child => child.id == id)
                    if (foundChild) return foundChild
                }

                return null
            }
        },
        departmentsCount: (state) => {
            let count = 0
            state.departments.forEach(dep => {
                count++
                count += dep.child.length
            })
            return count
        },
        getError: (state) => state.error,
        isInitialized: (state) => state._initialized
    },
    actions: {
        async init() {
            if (this._initialized) return
            
            this._authStore = useUserStore()
            
            try {
                this.loading = true;
                
                await Promise.all([
                    this.fetchOrganizationInfo(),
                    this.fetchDepartments(),
                    this.fetchPositions(),
                    this.fetchStatuses()
                ])
                this._initialized = true
            } catch (error) {
                this.error = error
                throw error
            } finally {
                this.loading = false
            }
        },         
        async fetchOrganizationInfo() {
            if (!this._authStore) this.init()
            try {
                this.loading = true;
                const data = await organizationApi.info(this._authStore.token)
                this.organizationInfo = data
                return data
            } catch (error) {
                this.error = error
                throw error;
            } finally {
                this.loading = false
            }
        },
    
        async fetchDepartments() {
            if (!this._authStore) this.init()
            try {
                this.loading = true
                const data = await organizationApi.getDepartments(this._authStore.token)
                this.$patch({
                    departments: data,
                    error: null
                });
                return data;
            } catch (error) {
                this.error = error
                throw error
            } finally {
                this.loading = false
            }
        },
    
        async fetchPositions() {
            if (!this._authStore) this.init()
            try {
                this.loading = true
                const data = await organizationApi.getPositions(this._authStore.token)
                this.$patch({
                    positions: data,
                    error: null
                });
                return data;
            } catch (error) {
                this.error = error
                throw error
            } finally {
                this.loading = false
            }
        },

        async fetchStatuses() {
            if (!this._authStore) this.init()
            try {
                this.loading = true
                const data = await organizationApi.getStatuses(this._authStore.token)
                this.statuses = data
                return data
            } catch (error) {
                this.error = error
                throw error
            } finally {
                this.loading = false
            }
        },
    
        async updateDepartment(data) {
            if (!this._authStore) this.init()
            try {
                this.loading = true
                const response = await organizationApi.updateDepartment(this._authStore.token, data)
                // Обновляем локальное состояние, если нужно
                await this.fetchDepartments(this._authStore.token)
                return response
            } catch (error) {
                this.error = error
                throw error
            } finally {
                this.loading = false
            }
        },

        async updatePosition(data) {
            if (!this._authStore) this.init()
            try {
                this.loading = true
                const response = await organizationApi.updatePosition(this._authStore.token, data)
                // Обновляем локальное состояние, если нужно
                await this.fetchPositions(this._authStore.token)
                return response
            } catch (error) {
                this.error = error
                throw error
            } finally {
                this.loading = false
            }
        },

        async updateEmployee(data) {
            if (!this._authStore) this.init()
            try {
                this.loading = true
                const response = await organizationApi.updateEmployee(this._authStore.token, data)
                // Обновляем локальное состояние, если нужно
                await this.fetchDepartments(this._authStore.token)
                return response
            } catch (error) {
                this.error = error
                throw error
            } finally {
                this.loading = false
            }
        },
        async regenerateAuthData(id) {
            if (!this._authStore) this.init()
            try {
                this.loading = true
                const response = await organizationApi.regenerateAuthData(this._authStore.token, id)
                return response
            } catch (error) {
                this.error = error
                throw error
            } finally {
                this.loading = false
            }
        },
        async removeEmployeeAvatar(id) {
            if (!this._authStore) this.init()
            try {
                this.loading = true
                const response = await organizationApi.removeEmployeeAvatar(this._authStore.token, id)
                // Обновляем локальное состояние, если нужно
                await this.fetchDepartments(this._authStore.token)
                return response
            } catch (error) {
                this.error = error
                throw error
            } finally {
                this.loading = false
            }
        }
    },
})